import React, { useState, useEffect, useCallback, useRef } from 'react';
import client from '../api/client';
import CollapsiblePanel from './CollapsiblePanel';
import { formatPrice } from '../utils/formatPrice';
import './RegionMarketStatsTable.css';
import './MarkupOpportunitiesSection.css';

const DEFAULT_THRESHOLD = 25;
const DEFAULT_STOCK_PERCENT = 50;
const STOCK_PERCENT_KEY = 'markupTargetStockPercent';
const DEBOUNCE_MS = 400;

const COLUMNS = [
  { key: 'type_name', label: 'Item Name' },
  { key: 'weekly_volume', label: 'Wk Volume', title: 'Average units sold per week across the region' },
  { key: null, label: 'Jita Price', title: 'Jita min sell' },
  { key: null, label: 'Landed Cost', title: 'Jita price + collateral + per-m³ shipping' },
  { key: null, label: 'Dest Price', title: 'Lowest sell order at this destination' },
  { key: 'markup', label: 'Markup', title: 'Destination price after broker fee, sales tax and SCC surcharge (structures only), over landed cost' },
  { key: null, label: 'Target Price', title: 'List price for your desired ROI' },
  { key: 'weekly_profit', label: 'Wk Profit', title: 'Weekly volume × profit per unit at the target price' },
  { key: null, label: '' },
];

function useDebounced(value) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [value]);
  return debounced;
}

const formatVolume = v => (v == null ? '—' : Math.round(v).toLocaleString());

function storedStockPercent() {
  try {
    return localStorage.getItem(STOCK_PERCENT_KEY) ?? String(DEFAULT_STOCK_PERCENT);
  } catch {
    return String(DEFAULT_STOCK_PERCENT);
  }
}

export default function MarkupOpportunitiesSection({ destinationId, refreshKey, onItemAdded }) {
  const [collapsed, setCollapsed] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [threshold, setThreshold] = useState(String(DEFAULT_THRESHOLD));
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ col: 'weekly_profit', order: 'desc' });
  const [page, setPage] = useState(1);
  const [fetchingVolumes, setFetchingVolumes] = useState(false);
  const [volumeProgress, setVolumeProgress] = useState(null);
  const pollRef = useRef(null);
  const [adding, setAdding] = useState(new Set());
  const [stockPercent, setStockPercent] = useState(storedStockPercent);

  useEffect(() => {
    try {
      localStorage.setItem(STOCK_PERCENT_KEY, stockPercent);
    } catch {
      // Storage unavailable; the setting just won't persist
    }
  }, [stockPercent]);

  // New items start with this percentage of a week's sales as target stock
  const initialTargetStock = row => Math.round((row.weekly_volume || 0) * (Number(stockPercent) || 0) / 100);

  const debouncedThreshold = useDebounced(threshold);
  const debouncedSearch = useDebounced(search);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await client.get(`/seeding/destinations/${destinationId}/markup-opportunities/`, {
        params: {
          threshold: Number(debouncedThreshold) || 0,
          search: debouncedSearch,
          sort: sort.col,
          order: sort.order,
          page,
        },
      });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load markup opportunities');
    } finally {
      setLoading(false);
    }
  }, [destinationId, debouncedThreshold, debouncedSearch, sort, page]);

  useEffect(() => {
    if (!collapsed) load();
  }, [collapsed, load, refreshKey]);

  // Filters change the result set, so go back to the first page
  useEffect(() => setPage(1), [debouncedThreshold, debouncedSearch]);

  const handleSort = col => {
    setSort(prev => (prev.col === col
      ? { col, order: prev.order === 'desc' ? 'asc' : 'desc' }
      : { col, order: col === 'type_name' ? 'asc' : 'desc' }));
    setPage(1);
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  const finishVolumeFetch = message => {
    clearInterval(pollRef.current);
    setFetchingVolumes(false);
    setVolumeProgress(null);
    if (message) setError(message);
    load();
  };

  const handleFetchVolumes = async () => {
    setFetchingVolumes(true);
    setError('');
    try {
      const response = await client.post(
        `/seeding/destinations/${destinationId}/markup-opportunities/fetch-volumes/`,
        { type_ids: data.missing_volume_type_ids },
      );
      if (!response.data.started) return finishVolumeFetch();
    } catch (err) {
      return finishVolumeFetch(err.response?.data?.error || 'Failed to fetch volumes');
    }

    // The fetch runs in the background; poll until it finishes
    pollRef.current = setInterval(async () => {
      try {
        const { data: progress } = await client.get(
          `/seeding/destinations/${destinationId}/progress/markup_volumes/`,
        );
        setVolumeProgress(progress);
        if (progress.status === 'completed') finishVolumeFetch();
        if (progress.status === 'error') finishVolumeFetch(progress.error_message || 'Failed to fetch volumes');
      } catch (err) {
        if (err.response?.status === 404) finishVolumeFetch();
      }
    }, 1500);
  };

  const handleAdd = async row => {
    setAdding(prev => new Set(prev).add(row.type_id));
    try {
      await client.post(`/seeding/destinations/${destinationId}/items/add/`, {
        type_id: row.type_id,
        target_stock: initialTargetStock(row),
      });
      setData(prev => ({
        ...prev,
        results: prev.results.map(r => (r.type_id === row.type_id ? { ...r, is_seeding_item: true } : r)),
      }));
      onItemAdded?.();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to add ${row.type_name}`);
    } finally {
      setAdding(prev => {
        const next = new Set(prev);
        next.delete(row.type_id);
        return next;
      });
    }
  };

  const sortArrow = col => {
    if (sort.col !== col) return <span className="sort-arrow sort-arrow--inactive">↕</span>;
    return <span className="sort-arrow">{sort.order === 'asc' ? '↑' : '↓'}</span>;
  };

  const { results = [], total = 0, pages = 0, missing_volume_type_ids: missing = [] } = data || {};

  return (
    <CollapsiblePanel
      title="Markup opportunities"
      sub="Destination items priced well above their landed cost from Jita"
      collapsed={collapsed}
      onToggle={() => setCollapsed(!collapsed)}
    >
        <div className="mo-content">
          <p className="mo-explainer">
            Items listed here whose sell price, after broker fee, sales tax and (at structures) SCC surcharge, beats the landed cost from
            Jita (min sell + collateral + shipping) by at least the threshold. Target price and weekly profit
            use your desired ROI of {data?.desired_roi_percentage ?? '—'}%.
          </p>

          <div className="toolbar">
            <label className="mo-threshold">
              Min markup
              <input
                type="number"
                min="0"
                step="5"
                value={threshold}
                onChange={e => setThreshold(e.target.value)}
              />
              %
            </label>
            <label className="mo-threshold" title="Target stock for items added with + Seed">
              Target stock
              <input
                type="number"
                min="0"
                step="10"
                value={stockPercent}
                onChange={e => setStockPercent(e.target.value)}
              />
              % of wk volume
            </label>
            <input
              type="text"
              className="rms-search-input"
              placeholder="Search item name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <span className="rms-total-count">{total.toLocaleString()} items</span>
            {missing.length > 0 && (
              <button className="btn" onClick={handleFetchVolumes} disabled={fetchingVolumes}>
                {!fetchingVolumes
                  ? `Fetch missing volumes (${missing.length})`
                  : volumeProgress?.total
                    ? `Fetching… ${volumeProgress.current} / ${volumeProgress.total}`
                    : 'Fetching…'}
              </button>
            )}
          </div>

          {data && !data.market_data_last_fetched && (
            <p className="region-overview-empty">
              No destination market data yet. Fetch market prices above to load this destination's orders.
            </p>
          )}
          {error && <div className="error-message">{error}</div>}

          <div className="table-scroll">
            <table className="data-table rms-table">
              <thead>
                <tr>
                  {COLUMNS.map((col, i) => (
                    <th
                      key={col.key || i}
                      className="rms-th sortable"
                      title={col.title}
                      onClick={col.key ? () => handleSort(col.key) : undefined}
                      style={col.key ? undefined : { cursor: 'default' }}
                    >
                      {col.label} {col.key && sortArrow(col.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && !data ? (
                  <tr><td colSpan={COLUMNS.length} className="rms-loading-cell">Loading…</td></tr>
                ) : results.length === 0 ? (
                  <tr><td colSpan={COLUMNS.length} className="rms-empty-cell">No items at this markup.</td></tr>
                ) : (
                  results.map(row => (
                    <tr
                      key={row.type_id}
                      className={`rms-row${row.is_seeding_item ? ' rms-row--seeding' : ''}${loading ? ' mo-row--stale' : ''}`}
                    >
                      <td className="rms-td rms-td--name">
                        {row.type_name}
                        {row.is_seeding_item && <span className="rms-seeding-badge">seeding</span>}
                      </td>
                      <td className="rms-td rms-td--number">{formatVolume(row.weekly_volume)}</td>
                      <td className="rms-td rms-td--number">{formatPrice(row.jita_price)}</td>
                      <td className="rms-td rms-td--number">{formatPrice(row.landed_cost)}</td>
                      <td className="rms-td rms-td--number">{formatPrice(row.destination_price)}</td>
                      <td className="rms-td rms-td--number mo-markup">{row.markup.toFixed(1)}%</td>
                      <td className="rms-td rms-td--number">{formatPrice(row.target_price)}</td>
                      <td className="rms-td rms-td--number">
                        {row.weekly_profit == null ? '—' : formatPrice(row.weekly_profit)}
                      </td>
                      <td className="rms-td">
                        {!row.is_seeding_item && (
                          <button
                            className="btn btn-sm"
                            onClick={() => handleAdd(row)}
                            title={`Add with target stock ${initialTargetStock(row).toLocaleString()} (${stockPercent || 0}% of weekly volume)`}
                            disabled={adding.has(row.type_id)}
                          >
                            {adding.has(row.type_id) ? 'Adding…' : '+ Seed'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="pager">
              <button className="btn btn-sm" onClick={() => setPage(page - 1)} disabled={page <= 1 || loading}>
                Previous
              </button>
              <span className="rms-page-info">Page {page} of {pages}</span>
              <button className="btn btn-sm" onClick={() => setPage(page + 1)} disabled={page >= pages || loading}>
                Next
              </button>
            </div>
          )}
        </div>
    </CollapsiblePanel>
  );
}
